import { Box, Button, createStyles, Grid, TextField, Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import React, { Dispatch, SetStateAction } from 'react';
import {
  arrayMove,
  SortableContainer as sortableContainer,
  SortableElement as sortableElement,
  SortableHandle as sortableHandle,
  SortableElementProps,
} from 'react-sortable-hoc';
import { IGiftboxStory, IStory } from '../../../services/types';
import useStyles from './styles';
import { VerticalEllipsisIcon } from '../../../assets/Svg';
import { getStoryById } from '../../../services/api/Story';

interface IProps {
  giftboxStories: IGiftboxStory[];
  setGiftboxStories: Dispatch<SetStateAction<IGiftboxStory[]>>;
  isLive?: boolean;
}
const DragHandle = sortableHandle(() => <VerticalEllipsisIcon />);

const GiftboxStory = sortableElement((props: any) => {
  const classes = useStyles();
  const { idx, story, setGiftboxStories, validateGiftboxStory, removeGiftboxStoryFromList, isLive } = props;

  return (
    <Box p={2} key={`gifbox-story-${idx}`} display="flex">
      <DragHandle />
      <Grid container alignItems="flex-start" justify="center">
        <Grid item xs={10}>
          <Grid container spacing={2}>
            <Grid item xs={4} className={classes.storyIdGrid}>
              <TextField
                label="Story ID"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                value={story.storyId}
                disabled={isLive && story.id !== undefined}
                error={story.error !== undefined}
                helperText={story.error}
                onChange={data => {
                  const input = data.target.value || '';
                  setGiftboxStories((prev: any) => {
                    prev[idx].storyId = input;
                    prev[idx].story = null;
                    prev[idx].isValid = false;
                    prev[idx].error = undefined;
                    return [...prev];
                  });
                }}
              />
              {story.story && <div className={classes.storyTitle}> {story.story.title} </div>}
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Number of Coupons"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                value={story.numberOfCoupons}
                disabled={isLive && story.id !== undefined}
                error={story.errorNumberOfCoupons}
                helperText="Required Field"
                onChange={data => {
                  const input = parseInt(data.target.value, 10) || '';
                  setGiftboxStories((prev: any) => {
                    prev[idx].numberOfCoupons = input;
                    prev[idx].errorNumberOfCoupons = false;
                    return [...prev];
                  });
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Validiity Period(Days)"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                value={story.validityPeriod}
                disabled={isLive && story.id !== undefined}
                error={story.errorValidityPeriod}
                helperText="Required Field"
                onChange={data => {
                  const input = parseInt(data.target.value, 10) || '';
                  setGiftboxStories((prev: any) => {
                    prev[idx].validityPeriod = input;
                    prev[idx].errorValidityPeriod = false;
                    return [...prev];
                  });
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={2}>
          {!story.isValid && (
            <Button
              color="primary"
              onClick={() => {
                validateGiftboxStory(story);
              }}
            >
              Validate Story
            </Button>
          )}
          {story.isValid && (
            <Button
              className={classes.hideButton}
              onClick={() => {
                removeGiftboxStoryFromList(story);
              }}
            >
              {isLive ? 'HIDE' : 'UNDO'}
            </Button>
          )}
        </Grid>
      </Grid>
    </Box>
  );
});

const SortableBox = sortableContainer((props: any) => {
  const { children, className } = props;
  return <Box className={className}>{children}</Box>;
});

const GiftboxStoryList = (props: IProps): JSX.Element => {
  const { giftboxStories, setGiftboxStories, isLive } = props;
  const classes = useStyles();

  const onSortEnd = ({ newIndex, oldIndex }: { newIndex: number; oldIndex: number }) => {
    const clonedGiftboxStories = [...giftboxStories];
    setGiftboxStories(arrayMove(clonedGiftboxStories, oldIndex, newIndex));
  };

  const validateGiftboxStory = async (giftboxStory: IGiftboxStory) => {
    let story;

    try {
      story = (await getStoryById(parseInt(giftboxStory.storyId, 10))).data as IStory;
      giftboxStory.story = story;
    } catch (e) {
      giftboxStory.error = 'Invalid Story ID';
    }

    if (isNaN(Number(giftboxStory.numberOfCoupons)) || Number(giftboxStory.numberOfCoupons) === 0) {
      giftboxStory.errorNumberOfCoupons = true;
    } else {
      giftboxStory.errorNumberOfCoupons = false;
    }

    if (isNaN(Number(giftboxStory.validityPeriod)) || Number(giftboxStory.validityPeriod) === 0) {
      giftboxStory.errorValidityPeriod = true;
    } else {
      giftboxStory.errorValidityPeriod = false;
    }

    if (story) {
      if (story.isDeleted === true) {
        giftboxStory.error = 'Invalid Story ID';
        giftboxStory.story = undefined;
      } else {
        const validStoryIds: string[] = giftboxStories.filter(gs => gs.isValid).map(gs => gs.storyId);
        if (validStoryIds.indexOf(giftboxStory.storyId) !== -1) {
          giftboxStory.error = 'Duplicate Story ID';
          giftboxStory.story = undefined;
        } else {
          giftboxStory.error = undefined;
        }
      }
    }

    if (giftboxStory.error !== undefined || giftboxStory.errorNumberOfCoupons == true || giftboxStory.errorValidityPeriod === true) {
      giftboxStory.isValid = false;
    } else {
      giftboxStory.isValid = true;
    }

    setGiftboxStories([...giftboxStories]);
  };

  const removeGiftboxStoryFromList = (giftboxStory: IGiftboxStory) => {
    if (isLive) {
      const clonedGiftboxStories = [...giftboxStories];
      clonedGiftboxStories.splice(giftboxStories.indexOf(giftboxStory), 1);
      setGiftboxStories(clonedGiftboxStories);
    } else {
      giftboxStory.storyId = '';
      giftboxStory.story = undefined;
      giftboxStory.validityPeriod = '';
      giftboxStory.id = undefined;
      giftboxStory.numberOfCoupons = '';
      giftboxStory.error = undefined;
      giftboxStory.errorNumberOfCoupons = undefined;
      giftboxStory.errorValidityPeriod = undefined;
      giftboxStory.isValid = false;
      setGiftboxStories([...giftboxStories]);
    }
  };

  return (
    <SortableBox Box lockAxis="y" useDragHandle={true} onSortEnd={onSortEnd} className={classes.giftboxStoryListBox}>
      {giftboxStories.map((story, idx) => {
        return (
          <GiftboxStory
            key={`sortable-giftbox-story-${idx}`}
            index={idx}
            idx={idx}
            story={story}
            isLive={isLive}
            classes={classes}
            giftboxStories={giftboxStories}
            setGiftboxStories={setGiftboxStories}
            validateGiftboxStory={validateGiftboxStory}
            removeGiftboxStoryFromList={removeGiftboxStoryFromList}
          ></GiftboxStory>
        );
      })}
    </SortableBox>
  );
};
export default GiftboxStoryList;
